import { Autocomplete, Box, Breadcrumbs, Button, Chip, FormLabel, Grid, InputAdornment, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import SearchIcon from '@mui/icons-material/Search';
import { useSupplierStore } from "../../hooks/useSupplierStore";
import { Supplier } from "../../types/slices/supplierType";
import { ApiStatus } from "../../types/api/status";
import { emphasize, styled } from '@mui/material/styles';
import { formatDateTime } from "../../toolbox/helpers/formatDateHelper";
import { moneyCL } from "../../toolbox/helpers/money-helpers";
import { usePaymentVoucherStore } from "../../hooks/usePaymentVoucherStore";
import CustomTable from "../../components/common/CustomTable/CustomTable";
import { useCartolaStore } from "../../hooks/useCartolaStore";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ModalConfirm } from "../../components/common/ModalConfirm/ModalConfirm";
import { Cartola as CartolaType } from "../../types/slices/cartolaType";
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ExcelIcon from '../../assets/fileIcons/xlsx.png';
import PDFIcon from '../../assets/fileIcons/pdf.png';

interface onList {
    page?: number
    perPage?: number
    search?: string
    idsupplier?: number
    orderBy?: string
    order?: string
    startDate?: string
    endDate?: string
}

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
    const backgroundColor =
        theme.palette.mode === 'light'
            ? theme.palette.grey[100]
            : theme.palette.grey[800];
    return {
        backgroundColor,
        height: theme.spacing(3),
        color: theme.palette.text.primary,
        fontWeight: theme.typography.fontWeightRegular,
        '&:hover, &:focus': {
            backgroundColor: emphasize(backgroundColor, 0.06),
        },
        '&:active': {
            boxShadow: theme.shadows[1],
            backgroundColor: emphasize(backgroundColor, 0.12),
        },
    };
})

const columns = [
    { type: 'text', field: 'created_at', label: 'Fecha', align: 'center', format: (row) => formatDateTime(row.created_at) },
    // { type: 'text', field: 'correlative', label: 'ID', align: 'center' },
    { type: 'text', field: 'n_document', label: 'N° Documento', align: 'center' },
    { type: 'text', field: 'type_name', label: 'Movimiento', align: 'center', format: (row) => row.type_name || '-' },
    { type: 'text', field: 'way_to_pay', label: 'Método de pago', align: 'center', format: (row) => row.way_to_pay || '-' },
    { type: 'text', field: 'charge', label: 'Cargo', align: 'right', format: (row) => moneyCL(row.charge) },
    { type: 'text', field: 'payment', label: 'Abono', align: 'right', format: (row) => moneyCL(row.payment) },
    // { type: 'text', field: 'balance', label: 'Saldo', align: 'right', format: (row) => moneyCL(row.balance) },
    { type: 'text', field: 'gloss', label: 'Glosa', align: 'center' },
    { type: 'detail', field: 'detail', label: '', align: 'center' },
];

const today = new Date().toISOString().split('T')[0]
const oneMonthAgo = new Date();
oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
const startOfMonth  = oneMonthAgo.toISOString().split('T')[0];

export const Cartola: FC = () => {
    const { status: statusSupplier, supppliers, getSuppliers } = useSupplierStore()
    const { status: statusCartola, cartolas, totalRows, getCartolas, clearCartolas,
        getCartolaExcel, getCartolaPDF
    } = useCartolaStore()
    const { status: statusPayment, cancelPaymentVoucher } = usePaymentVoucherStore()

    const [search, setSearch] = useState<string>('')
    const [page, setPage] = useState<number>(1)
    const [perPage, setPerPage] = useState<number>(25)

    const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null)
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false)
    const [rowSelected, setRowSelected] = useState<any>(null)

    const [startDate, setStartDate] = useState<string | null>(startOfMonth)
    const [endDate, setEndDate] = useState<string | null>(today)


    const searchRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        clearCartolas()
        getSuppliers()
    }, [selectedSupplier])

    useEffect(() => {
        if (search === '') {
            getDataAPI()
        } else {
            const timer = setTimeout(() => getDataAPI(), 500)
            return () => clearTimeout(timer)
        }
    }, [page, perPage, search, selectedSupplier, startDate, endDate])

    const getDataAPI = async () => {
        if (!selectedSupplier) return

        const payload: onList = {
            page: page,
            perPage: perPage,
            idsupplier: selectedSupplier?.id
        }
        if (search !== '') payload.search = search
        if (startDate) payload.startDate = startDate
        if (endDate) payload.endDate = endDate

        await getCartolas(payload)
    }


    const handleChangePage = (event: any, newPage: any) => {
        setPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (event) => {
        setPerPage(+event.target.value)
        setPage(1)
    }

    const onSearch = (search) => {
        setSearch(search)
    }

    const showDialog = (rowSelected) => {
        setRowSelected(rowSelected)
        setShowConfirmDialog(true)
    }

    const onCancel = async () => {
        const resp = await cancelPaymentVoucher(rowSelected?.idpayment_voucher)
        if (resp === true) {
            getDataAPI()
            setShowConfirmDialog(false)
        }
    }

    const onDetailCondition = (rowSelected: CartolaType) => rowSelected?.type === '2' && rowSelected?.status_voucher === '1'

    const onDetails = (rowSelected) => {
        if (!rowSelected?.url_document) return

        if (rowSelected.type == '1') {
            window.open(rowSelected.url_document, '_blank')
        } else {
            window.open(process.env.REACT_APP_ROOT_URL + rowSelected.url_document, '_blank')
        }
    }

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Shift') {
                searchRef.current?.focus()
                setSearch('')
            }

            else if (event.key === 'F3') {
                event.preventDefault()
                document.getElementById("idsupplier")?.focus()
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [])

    const onExportExcel = async () => {
        if (!selectedSupplier) return

        const resp = await getCartolaExcel({
            idsupplier: selectedSupplier?.id,
            startDate: startDate,
            endDate: endDate
        })

        if (resp !== '') {
            window.open(resp, '_blank')
        }
    }

    const onDownloadPDF = async () => {
        if (!selectedSupplier) return

        const resp = await getCartolaPDF({
            idsupplier: selectedSupplier?.id,
            startDate: startDate,
            endDate: endDate
        })

        if (resp !== '') {
            window.open(resp, '_blank')
        }
    }

    const lastBalance = cartolas.length > 0 ? cartolas[0]?.balance : 0;
    const saldo = lastBalance + (selectedSupplier?.balance_infavor || 0);

    return (
        <Grid container xs={12} spacing={2}>
            <Grid item xs={12}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 3 }}>
                    <StyledBreadcrumb
                        label={"Tesorería"}
                    />
                    <StyledBreadcrumb
                        label={"Cartola"}
                    />
                </Breadcrumbs>
            </Grid>
            <Grid item xs={12}
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    marginBottom: '10px'
                }}>
                <Typography variant='h6' component='h1' gutterBottom sx={{ mt: 1, ml: 1 }} className='title-main'>
                    CARTOLA DE CLIENTES
                </Typography>
            </Grid>
            <Grid item xs={12} sm={3} md={3}>
                <TextField
                    inputRef={searchRef}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        )
                    }}
                    onChange={(e) => {
                        onSearch(e.target.value);
                        setPage(0)
                    }}
                    placeholder="Buscar..."
                    size="small"
                />
            </Grid>
            <Grid item xs={12} sm={3} md={3}>
                <Autocomplete
                    id="idsupplier"
                    size="small"
                    value={selectedSupplier || null}
                    options={supppliers || []}
                    getOptionLabel={(option) => option.name_rz + ' - ' + option.rut}
                    isOptionEqualToValue={(option, value) => option.name_rz === value?.name_rz}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            label="Cliente"
                        />
                    )}
                    onChange={(e, newValue: any) => {
                        setSelectedSupplier(newValue)
                    }}
                    loading={statusSupplier === ApiStatus.FETCHING}
                    disabled={statusSupplier === ApiStatus.FETCHING}
                />
            </Grid>
            <Grid item xs={6} sm={3} md={3}>
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{ width: 'auto' }}
                >
                    <TextField
                        label="Desde"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        size="small"
                    />
                    <TextField
                        label="Hasta"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        size="small"
                    />
                </Stack>
            </Grid>
            <Grid item xs={6} sm={3} md={3}
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center'
                }}
            >
                <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="flex-end"
                    sx={{ width: 'auto' }}
                >

                    <Tooltip title="Descargar Excel">
                        <Button
                            variant="contained"
                            onClick={onExportExcel}
                            sx={{
                                backgroundColor: "#516E80",
                                color: "white",
                                '&:hover': {
                                    backgroundColor: "#3A556A"
                                },
                                display: 'flex',
                                alignItems: 'center',
                                gap: "8px"
                            }}
                            startIcon={<CloudDownloadIcon sx={{ color: 'white', fontSize: "20px" }} />}
                        >
                            <img
                                src={ExcelIcon}
                                alt="Excel"
                                style={{ width: '20px', height: '20px' }}
                            />
                        </Button>
                    </Tooltip>
                    <Tooltip title="Descargar PDF">
                        <Button
                            variant="contained"
                            onClick={onDownloadPDF}
                            sx={{
                                backgroundColor: "#516E80",
                                color: "white",
                                '&:hover': {
                                    backgroundColor: "#3A556A"
                                },
                                display: 'flex',
                                alignItems: 'center',
                                gap: "8px"
                            }}
                            startIcon={<CloudDownloadIcon sx={{ color: 'white', fontSize: "20px" }} />}
                        >
                            <img
                                src={PDFIcon}
                                alt="PDF"
                                style={{ width: '20px', height: '20px' }}
                            />
                        </Button>
                    </Tooltip>
                </Stack>
            </Grid>
            <Grid item xs={12}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px',
                    boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)',
                    marginBottom: '10px'
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
                        Saldo: <span style={{ color: saldo < 0 ? 'red' : 'green' }}>{moneyCL(saldo)}</span>
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
                        Saldo por procesar: <span style={{ color: '#FFA500' }}>{moneyCL(selectedSupplier?.balance_infavor || 0)}</span>
                    </Typography>
                </Box>
            </Grid>
            <Grid item xs={12}>
                <CustomTable
                    withoutTitle
                    columns={columns}
                    loading={statusCartola === ApiStatus.FETCHING}
                    rows={cartolas || []}
                    onRowClick={() => { }}
                    hasOptions
                    onSearch={onSearch}
                    onDetails={onDetails}

                    // onHelper3={showDialog}
                    // tooltip_helper3="Anular"
                    // icon_helper3={<DeleteOutlineIcon fontSize="small" />}
                    // onDetailCondition={onDetailCondition}

                    totalRows={totalRows}
                    rowsPerPage={perPage}
                    page={page}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    withCustomPagination
                    dense
                    withRowsColors
                />
            </Grid>
            {showConfirmDialog && (
                <ModalConfirm
                    open={showConfirmDialog}
                    closeModal={() => { setShowConfirmDialog(false) }}
                    onConfirm={onCancel}
                    status={statusPayment === ApiStatus.FETCHING}
                    title="Confirmar anulación"
                    text="¿Está seguro de anular el abono?"
                />
            )}
        </Grid>
    )
}